/* @flow */
import * as React from 'react';
import { graphql, compose } from 'react-apollo';
import gql from 'graphql-tag';

import AddedDeviceVerifierModal from 'app/components/AddedDeviceVerifierModal.jsx';
import Button from 'components/Button.jsx';
import Checkbox from 'components/Checkbox.jsx';
import ConfirmDialog from 'components/ConfirmDialog.jsx';
import devicesQuery from 'settings/queries/devicesQuery.gql';
import Divider from 'components/Divider.jsx';
import EventTracking from 'lib/EventTracking.js';
import Feature from 'components/Feature.jsx';
import Glyph from 'components/Glyph.jsx';
import LayerTrigger from 'components/LayerTrigger.jsx';
import Link from 'components/Link.jsx';
import LinkConstants from 'constants/LinkConstants';
import MediaObject from 'components/MediaObject.jsx';
import ModalTrigger from 'components/ModalTrigger.jsx';
import ResendConfirmationEmail from 'settings/components/ResendConfirmationEmail.jsx';
import Text from 'components/Text.jsx';
import TextWithGlyph from 'components/TextWithGlyph.jsx';
import Tooltip from 'components/Tooltip.jsx';

import ToastActions from 'actions/ToastActions';
import { mountModal } from 'lib/modal';
import { ensureLeading1, formatAsPhoneNumber } from 'lib/phoneNumbers';

import type GQLDevice from 'settings/components/DeviceSettings.jsx';

import './styles/DeviceItem.scss';

function getMessageType(device: $Shape<GQLDevice>): string {
  switch (device.type) {
    case 'email':
      return 'emails';
    case 'gcm':
      return 'push notifications';
    case 'apns':
      return 'push notifications';
    case 'sms':
      return 'text messages';
    default:
      return 'notifications';
  }
}

function getNotificationType(device: $Shape<GQLDevice>): string {
  switch (device.type) {
    case 'email':
      return 'email notifications';
    case 'gcm':
      return 'push notifications';
    case 'apns':
      return 'push notifications';
    case 'sms':
      return 'text notifications';
    default:
      return 'notifications';
  }
}

type Props = {
  remove: (input: number) => Promise<{ data: Object }>,
  update: (deviceId: number, enabled: boolean) => Promise<{ data: Object }>,
  device: $Shape<GQLDevice>,
  refetch?: Function,
  onlyOneEmail: ?boolean,
  onlyOneEnabledDevice: ?boolean,
};

type State = {
  isHovering: boolean,
};

class DeviceItem extends React.Component<Props, State> {
  state: State = {
    isHovering: false,
  };

  handleDeviceStatusChange = (device: $Shape<GQLDevice>) => {
    const handleConfirm = () => {
      this.props.update(device.id, !device.enabled).then(
        res => {
          ToastActions.showSuccessToast(
            res.data.enableOrDisableDevice.device.enabled
              ? `Notifications turned on for ${device.label}`
              : `Notifications turned off for ${device.label}`
          );
        },
        () => ToastActions.showDefaultErrorToast()
      );
    };

    if (device.enabled && this.props.onlyOneEnabledDevice) {
      EventTracking.trackKey(
        'account_settings.disable_notifications.only_enabled_device.checkbox.click'
      );
      mountModal(
        <ConfirmDialog
          title={
            <div>
              Are you sure you want to turn off {getNotificationType(device)} for
              <br />
              {device.label}?
            </div>
          }
          subtext="You’ll miss important messages without at least one type of notification."
          actionButtonText="Turn off"
          cancelButtonText="Cancel"
          onConfirm={() => {
            handleConfirm();
            EventTracking.trackKey(
              'account_settings.disable_notifications_confirm_modal.only_enabled_device.turn_off.click'
            );
          }}
        />,
        this
      );
      return;
    }

    if (!device.enabled) {
      handleConfirm();
      return;
    }

    EventTracking.trackKey('account_settings.disable_notifications.checkbox.click');
    mountModal(
      <ConfirmDialog
        title={
          <div>
            Turn off {getNotificationType(device)} for
            <br />
            {device.label}?
          </div>
        }
        actionButtonText="Turn off"
        cancelButtonText="Cancel"
        onConfirm={() => {
          handleConfirm();
          EventTracking.trackKey(
            'account_settings.disable_notifications_confirm_modal.turn_off.click'
          );
        }}
        subtext={`You will no longer get ${getMessageType(
          device
        )} when you receive messages on Remind.`}
      />,
      this
    );
  };

  renderSmsVerificationModal = () => {
    const { device } = this.props;
    mountModal(
      <AddedDeviceVerifierModal pendingSmsDeviceId={device.id} refetch={this.props.refetch} />,
      this
    );
  };

  renderUnverifiedDeviceSubtext = (device: $Shape<GQLDevice>) => {
    if (device.__typename === 'PendingSmsDevice') {
      return (
        <div>
          <Link
            onClick={() => {
              this.renderSmsVerificationModal();
              EventTracking.trackKey('account_settings.call_preferences.verify_device.click');
            }}>
            Verify or reject number
          </Link>
        </div>
      );
    }

    if (device.type === 'email') {
      if (device.deliverable) {
        return (
          <Text color="thunder" element="div">
            Pending confirmation.{' '}
            <ResendConfirmationEmail email={device}>Resend email</ResendConfirmationEmail>
          </Text>
        );
      }
      return (
        <Text color="thunder" element="div">
          Please{' '}
          <ResendConfirmationEmail email={device}>confirm this email</ResendConfirmationEmail> to
          receive notifications.
        </Text>
      );
    }
    return null;
  };

  defineDeviceLabel = (device: $Shape<GQLDevice>) => {
    let label;
    if (device.type === 'sms' || device.__typename === 'PendingSmsDevice') {
      label = formatAsPhoneNumber(ensureLeading1(device.label));
    } else {
      label = device.label || 'unnamed';
    }
    return label;
  };

  renderRemoveButton = (device: $Shape<GQLDevice>) => {
    const removeDevice = () => {
      this.props
        .remove(device.id)
        .then(
          () => ToastActions.showSuccessToast(`${device.label} removed`),
          () => ToastActions.showDefaultErrorToast()
        );
    };

    const modal = (
      <ConfirmDialog
        eventScreenName="UserEmailDevice"
        actionButtonText="Remove"
        cancelButtonText="Cancel"
        size="xsmall"
        glyph="caution"
        glyphColor="lava"
        title={`Remove ${this.defineDeviceLabel(device)}?`}
        subtext={`${this.defineDeviceLabel(
          device
        )} will no longer be associated with your account. You can always add it again!`}
        onConfirm={removeDevice}
      />
    );

    return (
      <ModalTrigger modal={modal}>
        <Button
          level="secondary"
          size="xsmall"
          color="lava"
          eventElementName="UserEmailDevice-Row-removeBtn">
          Remove
        </Button>
      </ModalTrigger>
    );
  };

  renderPhoneDetails = (device: $Shape<GQLDevice>) => {
    return (
      <div className="DeviceItem-info">
        <div>
          {device.model && (
            <Text>
              {device.model}
              {' - '}
            </Text>
          )}
          {device.type &&
            device.osVersion && (
              <span>
                <Text>{device.type === 'apns' ? 'iOS' : ''}</Text> <Text>{device.osVersion}</Text>
              </span>
            )}
        </div>
        {device.appVersion && (
          <div>
            <Text>Remind app version {device.appVersion}</Text>
          </div>
        )}
      </div>
    );
  };

  render() {
    const { device, onlyOneEmail } = this.props;
    const notTheOnlyEmail = (device.type === 'email' && !onlyOneEmail) || device.type !== 'email';
    const isPushDevice = device.type === 'apns' || device.type === 'gcm';
    const notAPendingSmsDevice = device.__typename !== 'PendingSmsDevice';

    return (
      <div
        onMouseEnter={() => this.setState({ isHovering: true })}
        onMouseLeave={() => this.setState({ isHovering: false })}
        className="DeviceItem">
        <MediaObject center gutter="small">
          <Checkbox
            onChange={() => this.handleDeviceStatusChange(device)}
            disabled={device.type === 'email' && !device.deliverable}
            checked={device.enabled}
          />
          <MediaObject figure="right" className="DeviceItem-device" center>
            <div>
              {device.verified ? (
                <React.Fragment>
                  <Text color="pitch" weight="semibold">
                    {isPushDevice && (
                      <Glyph name={device.type === 'apns' ? 'ios' : 'android'} size="medium" />
                    )}{' '}
                    {this.defineDeviceLabel(device)}
                  </Text>
                  {isPushDevice && this.renderPhoneDetails(device)}
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <Text color="thunder" weight="semibold">
                    {this.defineDeviceLabel(device)}{' '}
                  </Text>
                  {!device.verified && this.renderUnverifiedDeviceSubtext(device)}
                </React.Fragment>
              )}
              {device.reachabilityInfo &&
                device.reachabilityInfo.reasonShort &&
                device.reachabilityInfo.reasonLong && (
                  <Feature name="2018.oct.web.reachability_status">
                    <Link href={LinkConstants.UNREACHABLE_DEVICES} target="_blank">
                      <LayerTrigger
                        placement="left"
                        layer={<Tooltip>{device.reachabilityInfo.reasonLong}</Tooltip>}>
                        <TextWithGlyph
                          color="lava"
                          glyph="explanation-circle"
                          glyphColor="lava"
                          gutter="xxsmall">
                          {device.reachabilityInfo.reasonShort}
                        </TextWithGlyph>
                      </LayerTrigger>
                    </Link>
                  </Feature>
                )}
            </div>
            {this.state.isHovering &&
              notTheOnlyEmail &&
              notAPendingSmsDevice &&
              this.renderRemoveButton(device)}
          </MediaObject>
        </MediaObject>
        {!isPushDevice && (
          <div className="DeviceItem-dividerContainer">
            <Divider size="small" />
          </div>
        )}
      </div>
    );
  }
}

const withRemoveMutation = graphql(
  gql`
    mutation RemoveDeviceFromUser($input: Int!) {
      removeDevice(input: { deviceId: $input }) {
        error {
          message
          code
        }
      }
    }
  `,
  {
    props: ({ mutate }) => ({
      remove: deviceId =>
        mutate({
          variables: { input: deviceId },
          refetchQueries: [
            {
              query: devicesQuery,
            },
          ],
        }),
    }),
  }
);

const withEnableDisableMutation = graphql(
  gql`
    mutation EnableOrDisableDevice($deviceId: Int!, $enabled: Boolean!) {
      enableOrDisableDevice(input: { deviceId: $deviceId, enabled: $enabled }) {
        device {
          id
          enabled
        }
        error {
          message
          code
        }
      }
    }
  `,
  {
    props: ({ mutate }) => ({
      update: (deviceId, enabled) =>
        mutate({
          variables: { deviceId, enabled },
        }),
    }),
  }
);

export { DeviceItem };
export default compose(
  withRemoveMutation,
  withEnableDisableMutation
)(DeviceItem);
